import React, { useEffect } from "react";
import ReactPlayer from "react-player";
import { useRevampProductsStore } from "../store";
import { DetailsSkeleton } from "./ProductSkeleton";
import { getProductUniqueId } from "../helpers";
import { CDN_BASE_URL } from "../../../config/env";
import { startCase } from "lodash";

const DOSAGE_ICON_MAP = {
  "SunHorizon.webp": "/images/icons/sun.png",
  "Moon.webp": "/images/icons/moon.png",
  "Pill.webp": "/images/icons/pill.png",
};

const getDosageIconSrc = (iconPath) => {
  if (!iconPath) return null;
  const filename = iconPath.split("/").pop();
  return DOSAGE_ICON_MAP[filename] || `${CDN_BASE_URL}${iconPath}`;
};

const ProductDetailsDrawer = ({ product, visible, onClose }) => {
  const { productDetails, productDetailsLoading, fetchSingleProductDetails, clearProductDetails } =
    useRevampProductsStore();

  useEffect(() => {
    if (visible && product) {
      const productId = getProductUniqueId(product);
      fetchSingleProductDetails(productId);
    }
    return () => {
      if (!visible) clearProductDetails();
    };
  }, [visible, product]);

  if (!visible) return null;

  const info = productDetails;

  return (
    <>
      <div
        className={`revamp-details-overlay ${visible ? "revamp-details-overlay--visible" : ""}`}
        onClick={onClose}
      />
      <div className={`revamp-details-drawer ${visible ? "revamp-details-drawer--open" : ""}`}>
        <div className="revamp-details-drawer__header">
          <button className="revamp-details-drawer__back" onClick={onClose}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="15 18 9 12 15 6" />
            </svg>
            <span>Back</span>
          </button>
        </div>

        <div className="revamp-details-drawer__content">
          {productDetailsLoading ? (
            <DetailsSkeleton />
          ) : info ? (
            <div className="revamp-details">
              {info.product_image && (
                <div className="revamp-details__hero">
                  <img
                    src={`${CDN_BASE_URL}${info.product_image}`}
                    alt={info.product_name}
                    className="revamp-details__hero-image"
                  />
                </div>
              )}

              <div className="revamp-details__intro">
                <h2 className="revamp-details__name">{info.product_name}</h2>
                <div className="revamp-details__badges">
                  {info.type && (
                    <span className="revamp-details__badge revamp-details__badge--type">
                      {startCase(info.type)}
                    </span>
                  )}
                  {info.price?.amount && (
                    <span className="revamp-details__badge revamp-details__badge--price">
                      {info.price.currencyCode === "INR" ? "\u20B9" : ""}{info.price.amount}
                    </span>
                  )}
                  {info.quantity?.amount && (
                    <span className="revamp-details__badge revamp-details__badge--duration">
                      {info.quantity.amount} {info.quantity.metric}
                    </span>
                  )}
                  {info.course_duration && (
                    <span className="revamp-details__badge revamp-details__badge--course">
                      {info.course_duration}
                    </span>
                  )}
                </div>
              </div>

              {info.dosage_icons?.length > 0 && (
                <div className="revamp-details__section">
                  <h4 className="revamp-details__section-title">Dosage</h4>
                  <div className="revamp-details__dosage-icons">
                    {info.dosage_icons.map((d, idx) => (
                      <div key={`dosage-icon-${idx}`} className="revamp-details__dosage-chip">
                        {d.icon && <img src={getDosageIconSrc(d.icon)} alt={d.text} width="20" height="20" />}
                        <span>{d.text}</span>
                      </div>
                    ))}
                  </div>
                  {info.dosage && <p className="revamp-details__section-text">{info.dosage}</p>}
                </div>
              )}

              {(info.rootCause || info.contraindications || info.productBenefit) && (
                <div className="revamp-details__info-group">
                  {info.rootCause && (
                    <div className="revamp-details__info-row">
                      <span className="revamp-details__info-row-label">Root Cause</span>
                      <span className="revamp-details__info-row-value">{info.rootCause}</span>
                    </div>
                  )}
                  {info.productBenefit && (
                    <div className="revamp-details__info-row revamp-details__info-row--benefit">
                      <span className="revamp-details__info-row-label">Benefit</span>
                      <span className="revamp-details__info-row-value">{info.productBenefit}</span>
                    </div>
                  )}
                  {info.contraindications && (
                    <div className="revamp-details__info-row revamp-details__info-row--warning">
                      <span className="revamp-details__info-row-label">⚠ Contraindications</span>
                      <span className="revamp-details__info-row-value">{info.contraindications}</span>
                    </div>
                  )}
                </div>
              )}

              {info.supporting_icons?.length > 0 && (
                <div className="revamp-details__icons-row">
                  {info.supporting_icons.map((icon, idx) => (
                    <div key={`icon-${idx}`} className="revamp-details__supporting-icon">
                      <img src={`${CDN_BASE_URL}${icon.icon_url}`} alt={icon.icon_title} loading="lazy" />
                      <span>{icon.icon_title}</span>
                    </div>
                  ))}
                </div>
              )}

              {info.key_ingredients?.images?.male && (
                <div className="revamp-details__section">
                  <h4 className="revamp-details__section-title">{info.key_ingredients.title || "Key Ingredients"}</h4>
                  <img
                    src={`${CDN_BASE_URL}${info.key_ingredients.images.male}`}
                    alt="Key Ingredients"
                    className="revamp-details__ingredients-image"
                    loading="lazy"
                  />
                </div>
              )}

              {info.how_to_use?.media?.[0] && (
                <div className="revamp-details__section">
                  <div className="revamp-details__video-container">
                    <ReactPlayer
                      url={info.how_to_use.media[0].desktop_url}
                      controls
                      width="100%"
                      height="240px"
                      light={
                        info.how_to_use.media[0].thumbnail
                          ? `${CDN_BASE_URL}${info.how_to_use.media[0].thumbnail}`
                          : true
                      }
                      style={{ borderRadius: "12px", overflow: "hidden" }}
                    />
                  </div>
                  {info.how_to_use.title && (
                    <h4 className="revamp-details__section-title">{info.how_to_use.title}</h4>
                  )}
                  {info.how_to_use.description && (
                    <p className="revamp-details__section-text">{info.how_to_use.description}</p>
                  )}
                </div>
              )}

              {/* {info.product_media?.length > 0 && (
                <div className="revamp-details__section">
                  <h4 className="revamp-details__section-title">Product Gallery</h4>
                  <div className="revamp-details__gallery">
                    {info.product_media
                      .filter((m) => m.media_type === "image")
                      .map((media, idx) => (
                        <img
                          key={`media-${idx}`}
                          src={`${CDN_BASE_URL}${media.url}`}
                          alt={`Product ${idx + 1}`}
                          className="revamp-details__gallery-image"
                          loading="lazy"
                        />
                      ))}
                  </div>
                </div>
              )} */}
            </div>
          ) : (
            <div className="revamp-details__empty">
              <p>No product details available.</p>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default ProductDetailsDrawer;
